import { createViewportObserver } from "@solid-primitives/intersection-observer";
import { useLocation } from "@solidjs/router";
import { createEffect, createSignal } from "solid-js";

import { usePlayer } from "~/components/PlayerContext";
import rootStyles from "~/components/Root.module.scss";
import CastButton from "~/components/player/CastButton";
import Effect from "~/components/player/Effect";
import Progress from "~/components/player/Progress";
import RadioShowMetadata from "~/components/player/RadioShowMetadata";
import SongHistory from "~/components/player/SongHistory";
import SongHistoryButton from "~/components/player/SongHistoryButton";
import SongLyricsButton from "~/components/player/SongLyricsButton";
import SongRequestButton from "~/components/player/SongRequestButton";
import SourceSelect from "~/components/player/SourceSelect";
import Toolbar from "~/components/player/Toolbar";
import styles from "~/components/player/index.module.scss";
import { clickOutside } from "~/utils/clickOutside";

export default function Player() {
  const [isOpen, setIsOpen] = createSignal(false);
  const [isHidden, setIsHidden] = createSignal(true);
  const [showSongHistory, setShowSongHistory] = createSignal(false);
  const player = usePlayer();
  const [add, { remove }] = createViewportObserver({
    rootMargin: "-72px 0px 0px 0px",
  });
  const [isHomePlayerVisible, setIsHomePlayerVisible] = createSignal(false);
  const location = useLocation();

  let playerHome: HTMLDivElement | undefined;

  createEffect(() => {
    setIsHidden(!player.metadata || isHomePlayerVisible());
  });

  createEffect(() => {
    if (location.pathname === "/") {
      const element = document.querySelector<HTMLDivElement>("#player-home");
      if (!element) {
        setIsHomePlayerVisible(false);
        return;
      }

      playerHome = element;
      add(playerHome, (event) => setIsHomePlayerVisible(event.isIntersecting));
      return;
    }

    if (playerHome) {
      remove(playerHome);
    }

    setIsHomePlayerVisible(false);
  });

  return (
    <div
      classList={{
        [styles.wrapper]: true,
        [styles.hidden]: isHidden(),
        [styles.open]: isOpen(),
      }}
      use:clickOutside={() => {
        setIsOpen(false);
      }}
      onClick={(event) => {
        if (event.target.closest("a, button, input, select")) {
          return;
        }

        setIsOpen(true);
      }}
    >
      <Effect />
      <div classList={{ [styles.extra]: true, [rootStyles.container]: true }}>
        <div
          classList={{
            [styles["extra-panels"]]: true,
            [styles.alternate]: showSongHistory(),
          }}
        >
          <RadioShowMetadata />
          <SongHistory />
        </div>
        <div class={styles["extra-buttons"]}>
          <CastButton alternate />
          <SourceSelect alternate />
          <SongLyricsButton alternate />
          <SongHistoryButton
            onClick={() => {
              setShowSongHistory((old) => !old);
            }}
          />
          <SongRequestButton alternate />
        </div>
        <Progress alternate />
      </div>
      <div classList={{ [styles.player]: true, [rootStyles.container]: true }}>
        <Toolbar />
      </div>
    </div>
  );
}
